import React, { useCallback, useContext, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, UseFormSetError } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { useApiErrorHandler } from "@/lib/errorHandler";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { renderDateString, zodKeys } from "@/lib/utils";
import { Deadline, deadlinesApi } from "@/api/deadlines";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Case, casesApi } from "@/api/cases";
import { useListApi } from "@/hooks/useListApi";
import { DynamicSelectorV2 } from "../DynamicSelectorV2";
import { useTranslation } from "react-i18next";
import { DateFormField } from "@/components/custom/DateFormField";
import { User, userApi } from "@/api/users";
import { MultiSelect } from "@/components/ui/select";
import { useWatch } from "react-hook-form";
import { useOptimisticLocking } from "@/hooks/useOptimisticLocking";
import { ConflictResolutionDialog } from "@/components/ui/conflict-resolution-dialog";
import {
  withVersion,
  createOptimisticLockingToasts,
} from "@/lib/optimisticLockingHelpers";
import { deadlineFieldConfig } from "@/lib/fieldDiffUtils";

export function AddUpdateDeadline({
  open,
  setOpen,
  deadline,
  trigger,
  refresh,
  defaultCase,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deadline?: Deadline;
  trigger?: JSX.Element;
  refresh?: () => Promise<void>;
  defaultCase?: Case;
}) {
  const { t } = useTranslation();
  const { values: contextValues, set } = useContext(
    AuthContext,
  ) as AuthContextType;
  const { loading, user } = contextValues;
  const { setLoading } = set;

  const toasts = createOptimisticLockingToasts(
    t("deadlines.title.deadline"),
    t,
  );

  const {
    loading: casesLoading,
    search: caseSearch,
    loadNext: loadNextCase,
    list: casesList,
    apiResult: casesResult,
    pagination,
  } = useListApi<Case>({
    baseApiObject: casesApi,
    enable: open,
    additionalListIds: [deadline?.case?.toString()],
  });

  const { loading: usersLoading, list: usersList } = useListApi<User>({
    baseApiObject: userApi,
    defaultPageSize: 1000,
    enable: open,
  });

  const FormSchema = z.object({
    name: z.string(),
    date: z.date().nullable().optional(),
    notes: z.string(),
    private: z.boolean().default(false),
    case: z.string().min(1, {
      message: t("common.statictext.select_case"),
    }),
    // Only required when creating deadline.
    send_notification_emails: z.boolean().nullable().optional().default(false),
    notification_recipients: z.string().array().nullable().optional(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      date: null,
      notes: "",
      private: false,
      case: "",
      send_notification_emails: false,
      notification_recipients: [],
    },
  });

  const send_notification_emails = useWatch({
    name: "send_notification_emails",
    control: form.control,
  });

  // Function to get current form data for conflict comparison
  const getCurrentFormData = useCallback(
    (version: number): Deadline => {
      const formData = form.getValues();
      return {
        id: deadline?.id,
        version: version,
        case: parseInt(formData.case),
        name: formData.name,
        date: formData.date?.toLocaleDateString("sv") || "",
        notes: formData.notes,
        private: formData.private || false,
      } as Deadline;
    },
    [form, deadline?.id],
  );

  // Optimistic locking hook
  const {
    currentVersion,
    updateVersion,
    updateWithLocking,
    conflictState,
    handleRefresh,
    handleForceOverwrite,
    clearConflict,
  } = useOptimisticLocking<Deadline>({
    initialVersion: deadline?.version || 0,
    resourceId: deadline?.id || 0,
    resourceName: t("deadlines.title.deadline"),
    apiRead: (id) =>
      deadlinesApi.read({
        id: id.toString(),
        token: user?.access,
      }),
    apiUpdate: (id, data) =>
      deadlinesApi.update({
        id: id.toString() + "/",
        data,
        token: user?.access,
      }),
    onUpdateSuccess: () => {
      toast(toasts.updateSuccess);
    },
    onConflict: () => {
      toast(toasts.conflictDetected);
    },
    onUpdateError: (error) => {
      toast(toasts.error(error));
    },
    diffConfig: deadlineFieldConfig,
    localData: deadline ? getCurrentFormData(deadline.version) : undefined,
  });

  const errorHandler = useApiErrorHandler("AddUpdateDeadline", {
    setFormError: form.setError as UseFormSetError<Record<string, unknown>>,
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      setLoading?.(true);
      if (deadline && deadline.id) {
        // UPDATE with optimistic locking
        const result = await updateWithLocking(
          withVersion(
            {
              name: data.name,
              date: data.date?.toLocaleDateString("sv"),
              notes: data.notes,
              private: data.private,
              case: parseInt(data.case),
              updated_by: user?.user.id,
              send_notification_emails: data.send_notification_emails
                ? "on"
                : "off",
              notification_recipients:
                data.notification_recipients?.join(",") || "",
            },
            currentVersion,
          ),
        );

        if (result.conflict || !result.success) return;
      } else {
        await deadlinesApi.create({
          url: "/",
          data: {
            name: data.name,
            date: data.date?.toLocaleDateString("sv"),
            notes: data.notes,
            private: data.private,
            case: parseInt(data.case),
            send_notification_emails: data.send_notification_emails
              ? "on"
              : "off",
            notification_recipients:
              data.notification_recipients?.join(",") || "",
            created_by: user?.user.id,
          },
          token: user?.access,
        });
        toast({
          title: t("common.toast.added_successfully", {
            resource: t("deadlines.title.deadline"),
          }),
        });
        form.reset();
      }
      await refresh?.();
      setOpen(false);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading?.(false);
    }
  }

  useEffect(() => {
    if (deadline) {
      updateVersion(deadline.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...deadline,
            case: deadline.case.toString(),
            date: deadline.date
              ? new Date(renderDateString(deadline.date, true))
              : deadline.date,
            send_notification_emails: false,
            notification_recipients: [],
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);

  useEffect(() => {
    if (defaultCase?.id && open) {
      form.setValue("case", defaultCase.id.toString(), { shouldTouch: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCase, open]);

  const buttonAction = deadline?.id
    ? t("common.button.update")
    : t("common.button.add");

  // Conflict resolution handlers
  const handleRefreshDeadline = async () => {
    if (!deadline?.id) return;
    const refreshed = await handleRefresh();
    if (refreshed) {
      // Update form with refreshed data
      updateVersion(refreshed.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...refreshed,
            case: refreshed.case.toString(),
            date: refreshed.date
              ? new Date(renderDateString(refreshed.date, true))
              : refreshed.date,
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
      toast(toasts.refreshSuccess);
    }
  };

  const handleForceOverwriteDeadline = async () => {
    if (!conflictState.latestVersion || !deadline?.id) return;
    const formData = form.getValues();
    const result = await handleForceOverwrite(
      withVersion(
        {
          name: formData.name,
          date: formData.date?.toLocaleDateString("sv"),
          notes: formData.notes,
          private: formData.private,
          case: parseInt(formData.case),
          updated_by: user?.user.id,
        },
        conflictState.latestVersion,
      ),
    );
    if (result.success) {
      toast(toasts.overwriteSuccess);
      await refresh?.();
      setOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {!!trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          className="w-full md:w-[500px] max-h-screen overflow-y-auto"
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full flex flex-col gap-8"
            >
              <DialogHeader>
                <DialogTitle>
                  {buttonAction} {t("deadlines.title.deadline")}
                </DialogTitle>
              </DialogHeader>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* {t("common.label.name")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("deadlines.placeholder.name")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="private"
                render={({ field }) => (
                  <div>
                    <FormItem className="flex flex-row items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="!mt-0">
                        {t("common.label.private")}
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <DateFormField
                    control={form.control}
                    name="date"
                    label={t("common.label.date")}
                    description={t("deadlines.statictext.date")}
                    required={true}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common.label.notes")}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("deadlines.placeholder.notes")}
                        className="resize-none"
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="send_notification_emails"
                render={({ field }) => (
                  <div className="flex flex-col gap-1">
                    <FormItem className="flex flex-row items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="!mt-0">
                        {t("deadlines.label.send_notification_emails")}
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                    <FormDescription>
                      {t(
                        "deadlines.statictext.send_notification_email_description",
                      )}
                    </FormDescription>
                  </div>
                )}
              />

              {send_notification_emails && (
                <FormField
                  control={form.control}
                  name="notification_recipients"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("deadlines.label.notification_recipients")}{" "}
                        {usersLoading
                          ? t("common.button.loading_with_parens")
                          : ""}
                      </FormLabel>
                      <MultiSelect
                        selected={field.value || []}
                        options={usersList
                          .filter((item) => item.email)
                          .map((item) => ({
                            // eslint-disable-next-line @bigbinary/neeto/hard-coded-strings-should-be-localized
                            label: `${item.first_name} ${item.last_name} (${item.email})`,
                            value: item.id.toString(),
                          }))}
                        {...field}
                        className="sm:w-[510px]"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {!defaultCase ? (
                <FormField
                  control={form.control}
                  name="case"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("common.label.case")}{" "}
                        {casesLoading
                          ? t("common.button.loading_with_parens")
                          : ""}
                      </FormLabel>
                      <DynamicSelectorV2
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                        options={casesList.map((item) => ({
                          label: item.name,
                          value: item.id.toString(),
                        }))}
                        className="selector"
                        onLoadNext={loadNextCase}
                        onSearch={(searchValue) => {
                          caseSearch.set(searchValue);
                        }}
                        placeholder={t("deadlines.placeholder.case")}
                        totalCount={casesResult?.count || 0}
                        pageSize={pagination.pageSize}
                        currentPage={pagination.page}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                defaultCase.name
              )}

              <DialogFooter>
                <Button disabled={loading} type="submit">
                  {loading ? t("common.button.loading") : buttonAction}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <ConflictResolutionDialog
        open={conflictState.isOpen}
        onOpenChange={(open) => !open && clearConflict()}
        currentVersion={currentVersion}
        latestVersion={conflictState.latestVersion}
        resourceName={t("deadlines.title.deadline")}
        onRefresh={handleRefreshDeadline}
        onOverwrite={handleForceOverwriteDeadline}
        loading={loading}
        fieldDiffs={conflictState.fieldDiffs}
        fieldLabels={Object.fromEntries(
          Object.entries(deadlineFieldConfig.fields).map(([field, config]) => [
            field,
            t(config.label),
          ]),
        )}
      />
    </>
  );
}
